import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Trash2, Check, X, Send, Loader2,
  Cable, FileText as FT, History as HistoryIcon, RefreshCcw, Copy, Edit3,
  Wifi, WifiOff, HelpCircle, TestTube2,
} from "lucide-react";

const CATEGORY_BADGE = {
  cobranca: "bg-red-500/10 text-red-300 border-red-500/40",
  recuperacao: "bg-orange-500/10 text-orange-300 border-orange-500/40",
  acordos: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
  avisos: "bg-amber-500/10 text-amber-300 border-amber-500/40",
  marketing: "bg-purple-500/10 text-purple-300 border-purple-500/40",
  operacional: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40",
};

const STATUS_BADGE = {
  pending: "bg-[#262626] text-[#A3A3A3] border-[#3a3a3a]",
  sent: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40",
  delivered: "bg-blue-500/10 text-blue-300 border-blue-500/40",
  read: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
  error: "bg-red-500/10 text-red-300 border-red-500/40",
};

const STATUS_LABEL = {
  pending: "Pendente", sent: "Enviado", delivered: "Entregue", read: "Lido", error: "Erro",
};

const ORIGIN_LABEL = { manual: "Manual", bulk: "Em massa", automatic: "Automático" };

export default function WhatsAppPage() {
  const [tab, setTab] = useState("conexao");
  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">Administração · V2</div>
        <h1 className="font-display text-3xl sm:text-[34px] font-bold tracking-tight mt-1 flex items-center gap-2" data-testid="whatsapp-title">
          <MessageSquare size={26} className="text-emerald-400"/> WhatsApp
        </h1>
        <p className="text-[#A3A3A3] text-[13px] sm:text-sm mt-1.5 max-w-2xl">
          Infraestrutura provider-agnóstica. Configure a conexão, gerencie templates e acompanhe o histórico de mensagens enviadas.
        </p>
      </div>

      <div className="flex gap-2 mb-5 border-b border-[#1a1a1a]" data-testid="whatsapp-tabs">
        {[
          { v: "conexao", l: "Conexão", icon: Cable },
          { v: "teste", l: "Teste rápido", icon: TestTube2 },
          { v: "templates", l: "Templates", icon: FT },
          { v: "historico", l: "Histórico", icon: HistoryIcon },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 text-[13px] flex items-center gap-2 border-b-2 transition-colors ${
                tab === t.v ? "border-emerald-400 text-emerald-300" : "border-transparent text-[#A3A3A3] hover:text-[#E5E5E5]"
              }`}
              data-testid={`tab-${t.v}`}
            >
              <Icon size={14}/> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "conexao" && <ConexaoTab/>}
      {tab === "teste" && <TesteTab/>}
      {tab === "templates" && <TemplatesTab/>}
      {tab === "historico" && <HistoricoTab/>}
    </Layout>
  );
}

// =================== CONEXÃO ===================
function ConexaoTab() {
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [connStates, setConnStates] = useState({}); // { id: {state, checking} }

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/whatsapp/config"),
        api.get("/whatsapp/stats").catch(() => ({ data: null })),
      ]);
      setConfigs(c.data);
      setStats(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const testConnection = async (id) => {
    setConnStates(prev => ({ ...prev, [id]: { checking: true } }));
    try {
      const { data } = await api.get(`/whatsapp/config/${id}/connection-state`);
      setConnStates(prev => ({ ...prev, [id]: { state: data.state, raw: data.raw, error: data.error, checking: false } }));
      if (data.state === "connected") toast.success("Conectado ao provider");
      else if (data.state === "waiting_qr") toast.info("Aguardando escaneamento do QR code");
      else if (data.state === "disconnected") toast.warning("Instância desconectada");
      else if (data.state === "error") toast.error(data.error || "Erro de conexão");
      else toast.info(`Estado: ${data.state}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao testar conexão");
      setConnStates(prev => ({ ...prev, [id]: { state: "error", checking: false } }));
    }
  };

  const setDefault = async (id) => {
    try {
      await api.patch(`/whatsapp/config/${id}/set-default`);
      toast.success("Definido como padrão");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover essa configuração?")) return;
    try {
      await api.delete(`/whatsapp/config/${id}`);
      toast.success("Removido");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha"); }
  };

  return (
    <div>
      {/* Stats top */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Contas ativas" value={configs.filter(c=>c.active).length}/>
        <StatCard label="Templates" value={stats?.templates_count ?? "—"}/>
        <StatCard label="Mensagens enviadas" value={stats?.total_messages ?? 0}/>
        <StatCard label="Pendentes" value={stats?.by_status?.pending ?? 0} accent="amber"/>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[15px] font-semibold text-[#E5E5E5]">Contas de WhatsApp</h2>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="fc-btn-primary text-xs flex items-center gap-1.5" data-testid="btn-new-config">
          <Plus size={13}/> Nova conta
        </button>
      </div>

      {loading ? (
        <div className="fc-card p-8 text-center text-[#737373]"><Loader2 className="animate-spin mx-auto" size={18}/></div>
      ) : configs.length === 0 ? (
        <div className="fc-card p-10 text-center text-[#A3A3A3]" data-testid="config-empty">
          <MessageSquare size={32} className="mx-auto text-[#3a3a3a] mb-3"/>
          <div className="text-[14px]">Nenhuma conta configurada.</div>
          <div className="text-[12px] mt-1">Clique em &quot;Nova conta&quot; para começar.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {configs.map(c => {
            const cs = connStates[c.id] || {};
            const stateBadge = cs.state === "connected"
              ? { c: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", icon: Wifi, label: "Conectado" }
              : cs.state === "waiting_qr"
                ? { c: "text-amber-300 border-amber-500/40 bg-amber-500/10", icon: HelpCircle, label: "Aguarda QR" }
                : cs.state === "disconnected"
                  ? { c: "text-red-300 border-red-500/40 bg-red-500/10", icon: WifiOff, label: "Desconectado" }
                  : cs.state === "error"
                    ? { c: "text-red-300 border-red-500/40 bg-red-500/10", icon: WifiOff, label: "Erro" }
                    : null;
            const SBI = stateBadge?.icon;
            return (
            <div key={c.id} className="fc-card p-4 flex flex-wrap items-start gap-4" data-testid={`config-${c.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-tab text-[#E5E5E5] text-[15px]">+{c.phone_number}</span>
                  {c.is_default && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Padrão</span>}
                  {!c.active && <span className="text-[10px] px-2 py-0.5 rounded bg-[#262626] text-[#737373]">Inativo</span>}
                  {stateBadge && (
                    <span className={`text-[10px] px-2 py-0.5 rounded border inline-flex items-center gap-1 ${stateBadge.c}`} data-testid={`state-${c.id}`}>
                      <SBI size={10}/> {stateBadge.label}
                    </span>
                  )}
                </div>
                <div className="text-[11.5px] text-[#A3A3A3] mt-1">
                  <span className="text-[#737373]">Provider:</span> {c.provider}
                  {c.instance_name && <> · <span className="text-[#737373]">Instance:</span> <span className="font-mono-tab text-[#D4AF37]">{c.instance_name}</span></>}
                  {c.phone_label && <> · <span className="text-[#737373]">Rótulo:</span> {c.phone_label}</>}
                </div>
                <div className="text-[11px] text-[#737373] mt-0.5 font-mono-tab">
                  {c.api_url || "—"} · token: {c.token_masked || "—"}
                </div>
                {c.webhook_secret_masked && (
                  <div className="text-[10.5px] text-[#737373] mt-1">
                    Webhook path: <code className="text-emerald-300/90 select-all font-mono-tab">/api/whatsapp/webhook/&lt;secret&gt;</code>
                    <span className="ml-1">— registre a URL absoluta nesse formato na Evolution API</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => testConnection(c.id)} disabled={cs.checking} className="fc-btn-ghost text-xs flex items-center gap-1" data-testid={`test-conn-${c.id}`}>
                  {cs.checking ? <Loader2 size={13} className="animate-spin"/> : <Wifi size={13}/>} Testar
                </button>
                {!c.is_default && (
                  <button onClick={() => setDefault(c.id)} className="fc-btn-ghost text-xs flex items-center gap-1" title="Definir como padrão">
                    <Check size={13}/> Padrão
                  </button>
                )}
                <button onClick={() => { setEditing(c); setShowModal(true); }} className="fc-btn-ghost text-xs flex items-center gap-1">
                  <Edit3 size={13}/> Editar
                </button>
                <button onClick={() => remove(c.id)} className="text-red-300 hover:text-red-200 fc-btn-ghost text-xs flex items-center gap-1">
                  <Trash2 size={13}/> Excluir
                </button>
              </div>
            </div>
          );})}
        </div>
      )}

      {showModal && <ConfigModal editing={editing} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }}/>}
    </div>
  );
}

function StatCard({ label, value, accent = "gold" }) {
  const cls = { gold: "text-[#D4AF37] border-[#D4AF37]/30", amber: "text-amber-300 border-amber-500/30", emerald: "text-emerald-300 border-emerald-500/30" }[accent];
  return (
    <div className="fc-card p-3.5">
      <div className="text-[10.5px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono-tab text-lg mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function ConfigModal({ editing, onClose, onSaved }) {
  const [form, setForm] = useState({
    provider: editing?.provider || "evolution",
    phone_number: editing?.phone_number || "",
    phone_label: editing?.phone_label || "",
    instance_name: editing?.instance_name || "",
    token: "",
    api_url: editing?.api_url || "",
    webhook_secret: "",
    is_default: editing?.is_default ?? false,
    active: editing?.active ?? true,
    notes: editing?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/whatsapp/config/${editing.id}`, form);
        toast.success("Configuração atualizada");
      } else {
        await api.post("/whatsapp/config", form);
        toast.success("Configuração criada");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="fc-card p-6 w-full max-w-lg" data-testid="config-modal">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-display text-lg font-bold">{editing ? "Editar" : "Nova"} conta WhatsApp</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <label className="col-span-1">
            <div className="text-[#A3A3A3] mb-1">Provider *</div>
            <select className="fc-input" value={form.provider} onChange={e=>setForm({...form, provider:e.target.value})} data-testid="config-provider">
              <option value="evolution">Evolution API</option>
              <option value="mock">Mock (dev)</option>
              <option value="meta_cloud">Meta Cloud API (em breve)</option>
              <option value="zapi">Z-API (em breve)</option>
            </select>
          </label>
          <label>
            <div className="text-[#A3A3A3] mb-1">Rótulo</div>
            <input className="fc-input" value={form.phone_label} onChange={e=>setForm({...form, phone_label:e.target.value})} placeholder="Ex: Principal, Cobrança" data-testid="config-label"/>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1">Número (DDI+DDD+número) *</div>
            <input className="fc-input font-mono-tab" value={form.phone_number} onChange={e=>setForm({...form, phone_number:e.target.value})} placeholder="5511999999999" data-testid="config-phone"/>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1">
              Instance name (Evolution API) {form.provider === "evolution" && <span className="text-red-300">*</span>}
            </div>
            <input className="fc-input font-mono-tab" value={form.instance_name} onChange={e=>setForm({...form, instance_name:e.target.value})} placeholder="ex: forte-capital" data-testid="config-instance"/>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1">API URL {form.provider === "evolution" && <span className="text-red-300">*</span>}</div>
            <input className="fc-input" value={form.api_url} onChange={e=>setForm({...form, api_url:e.target.value})} placeholder="https://evo.example.com" data-testid="config-api-url"/>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1">Token {editing && <span className="text-[10px] text-[#737373]">(deixe vazio para manter)</span>}</div>
            <input type="password" className="fc-input" value={form.token} onChange={e=>setForm({...form, token:e.target.value})} placeholder={editing ? "••••••••" : "seu-token-secreto"} data-testid="config-token"/>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1">Webhook secret</div>
            <input type="password" className="fc-input" value={form.webhook_secret} onChange={e=>setForm({...form, webhook_secret:e.target.value})} placeholder={editing ? "••••••••" : "opcional"}/>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1">Observações</div>
            <textarea rows={2} className="fc-input" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/>
          </label>
          <label className="flex items-center gap-2 col-span-1">
            <input type="checkbox" checked={form.is_default} onChange={e=>setForm({...form, is_default:e.target.checked})} data-testid="config-default"/>
            <span>Definir como padrão</span>
          </label>
          <label className="flex items-center gap-2 col-span-1">
            <input type="checkbox" checked={form.active} onChange={e=>setForm({...form, active:e.target.checked})}/>
            <span>Ativa</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button
            onClick={save}
            disabled={saving || (form.provider === "evolution" && (!form.instance_name || !form.api_url || (!editing && !form.token))) || !form.phone_number}
            className="fc-btn-primary text-xs flex items-center gap-2"
            data-testid="config-save"
          >
            {saving && <Loader2 size={13} className="animate-spin"/>} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

// =================== TESTE RÁPIDO ===================
function TesteTab() {
  const [config, setConfig] = useState(null);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("Olá! Esta é uma mensagem de teste enviada pelo painel Forte Capital via Evolution API.");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, msg, data }
  const [connState, setConnState] = useState(null);
  const [checkingConn, setCheckingConn] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/config/default").then(r => setConfig(r.data)).catch(() => setConfig(null));
  }, []);

  const checkConn = async () => {
    if (!config) return;
    setCheckingConn(true);
    try {
      const { data } = await api.get(`/whatsapp/config/${config.id}/connection-state`);
      setConnState(data);
    } catch (err) {
      setConnState({ state: "error", error: err?.response?.data?.detail || "Falha" });
    } finally { setCheckingConn(false); }
  };

  const normalizePhone = (v) => v.replace(/\D/g, "");

  const send = async () => {
    setResult(null);
    const cleanPhone = normalizePhone(phone);
    if (cleanPhone.length < 10) {
      setResult({ ok: false, msg: "Número inválido. Formato: 55 + DDD + número (ex: 5511999999999)" });
      return;
    }
    if (!message || message.trim().length < 2) {
      setResult({ ok: false, msg: "Digite uma mensagem" });
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/send", {
        phone_to: cleanPhone,
        content: message,
        category: "operacional",
        origin: "manual",
      });
      const success = data.status === "sent" || data.status === "delivered" || data.status === "read";
      setResult({
        ok: success,
        msg: success
          ? `✅ Mensagem enviada com sucesso — status: ${data.status}`
          : data.status === "pending"
            ? `⏳ Mensagem registrada (provider ainda não respondeu). Status: ${data.status}`
            : `❌ Falha ao enviar. Status: ${data.status}`,
        data,
      });
      if (success) toast.success("Mensagem enviada!");
      else if (data.status === "error") toast.error(data.error_message || "Provider retornou erro");
      else toast.info(`Status: ${data.status}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erro desconhecido";
      setResult({ ok: false, msg: `❌ ${detail}`, data: null });
      toast.error(detail);
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="fc-card p-5 mb-4" data-testid="teste-config-summary">
        <div className="text-[11px] uppercase tracking-wider text-[#737373] mb-2">Configuração ativa</div>
        {config ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap text-[12.5px]">
              <span className="text-[#737373]">Base URL:</span>
              <span className="font-mono-tab text-emerald-300 select-all">{config.api_url || "—"}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-[12.5px]">
              <span className="text-[#737373]">Instance:</span>
              <span className="font-mono-tab text-[#D4AF37]">{config.instance_name || "—"}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-[12.5px]">
              <span className="text-[#737373]">Número:</span>
              <span className="font-mono-tab text-[#E5E5E5]">+{config.phone_number}</span>
              {config.phone_label && <span className="text-[#737373] text-[11px]">({config.phone_label})</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap text-[12.5px]">
              <span className="text-[#737373]">API Key:</span>
              <span className="font-mono-tab text-[#A3A3A3]">{config.token_masked || "—"}</span>
              <span className="text-[10px] text-[#737373]">(nunca sai do backend)</span>
            </div>
            <div className="pt-2 flex gap-2 items-center">
              <button onClick={checkConn} disabled={checkingConn} className="fc-btn-ghost text-xs flex items-center gap-1.5" data-testid="teste-check-conn">
                {checkingConn ? <Loader2 size={12} className="animate-spin"/> : <Wifi size={12}/>} Verificar conexão
              </button>
              {connState && (
                <span
                  className={`text-[11px] px-2 py-0.5 rounded border ${
                    connState.state === "connected" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" :
                    connState.state === "waiting_qr" ? "text-amber-300 border-amber-500/40 bg-amber-500/10" :
                    "text-red-300 border-red-500/40 bg-red-500/10"
                  }`}
                  data-testid="teste-conn-state"
                >
                  {connState.state === "connected" ? "Conectado" :
                   connState.state === "waiting_qr" ? "Aguardando QR" :
                   connState.state === "disconnected" ? "Desconectado" :
                   connState.state === "error" ? `Erro: ${connState.error || "—"}` : connState.state}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-[12px] text-amber-300" data-testid="teste-no-config">
            Nenhuma configuração ativa. Vá para a aba <strong>Conexão</strong> e clique em <strong>Nova conta</strong>.
          </div>
        )}
      </div>

      <div className="fc-card p-5" data-testid="teste-form">
        <div className="text-[11px] uppercase tracking-wider text-emerald-300 mb-1">Envio de teste</div>
        <h3 className="font-display text-lg font-semibold mb-4">Enviar WhatsApp de teste</h3>

        <label className="block mb-3">
          <div className="text-[11px] text-[#737373] uppercase mb-1.5">Número (DDI + DDD + número)</div>
          <input
            type="tel"
            className="fc-input font-mono-tab"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="5511999999999"
            data-testid="teste-phone"
          />
          <div className="text-[10.5px] text-[#737373] mt-1">
            Formato E.164 sem símbolos. Ex: <span className="font-mono-tab">5511987654321</span>
          </div>
        </label>

        <label className="block mb-4">
          <div className="text-[11px] text-[#737373] uppercase mb-1.5">Mensagem</div>
          <textarea
            rows={4}
            className="fc-input text-[13px]"
            value={message}
            onChange={e => setMessage(e.target.value)}
            data-testid="teste-message"
          />
          <div className="text-[10.5px] text-[#737373] mt-1">{message.length} caracteres</div>
        </label>

        <button
          onClick={send}
          disabled={sending || !config || !phone || !message}
          className="fc-btn-primary text-sm flex items-center gap-2"
          data-testid="teste-send"
        >
          {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
          Enviar WhatsApp de teste
        </button>

        {result && (
          <div
            className={`mt-4 p-3 rounded-md border text-[12.5px] ${
              result.ok
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/40 bg-red-500/10 text-red-200"
            }`}
            data-testid="teste-result"
          >
            <div className="font-medium">{result.msg}</div>
            {result.data && (
              <div className="mt-2 text-[11px] font-mono-tab text-[#A3A3A3] space-y-0.5">
                <div>ID interno: <span className="text-[#E5E5E5]">{result.data.id}</span></div>
                {result.data.provider_message_id && <div>ID Evolution: <span className="text-[#E5E5E5]">{result.data.provider_message_id}</span></div>}
                {result.data.error_message && <div className="text-red-300">Erro: {result.data.error_message}</div>}
              </div>
            )}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-[#1a1a1a] text-[11px] text-[#737373]">
          <div className="flex items-start gap-1.5">
            <TestTube2 size={12} className="mt-0.5 text-[#D4AF37]"/>
            <div>
              A mensagem é registrada no <strong className="text-[#A3A3A3]">Histórico</strong> com <span className="font-mono-tab">origin=&quot;manual&quot;</span> e <span className="font-mono-tab">category=&quot;operacional&quot;</span>.
              A API Key nunca é exposta ao browser — a chamada HTTP para a Evolution sai do backend Python.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== TEMPLATES ===================
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [categories, setCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/whatsapp/templates", { params: filterCat ? { category: filterCat } : {} }),
        api.get("/whatsapp/categories"),
      ]);
      setItems(t.data);
      setCategories(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterCat]);

  const remove = async (id) => {
    if (!window.confirm("Excluir template?")) return;
    try {
      await api.delete(`/whatsapp/templates/${id}`);
      toast.success("Removido");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={()=>setFilterCat("")} className={`text-xs px-3 py-1.5 rounded-md border ${filterCat===""?"bg-emerald-500/10 text-emerald-200 border-emerald-500/50":"border-[#262626] text-[#A3A3A3]"}`} data-testid="tpl-filter-all">Todas</button>
          {categories.map(c=>(
            <button key={c.code} onClick={()=>setFilterCat(c.code)} className={`text-xs px-3 py-1.5 rounded-md border ${filterCat===c.code?"bg-emerald-500/10 text-emerald-200 border-emerald-500/50":"border-[#262626] text-[#A3A3A3]"}`} data-testid={`tpl-filter-${c.code}`}>
              {c.label}
            </button>
          ))}
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="fc-btn-primary text-xs flex items-center gap-1.5" data-testid="btn-new-template">
          <Plus size={13}/> Novo template
        </button>
      </div>

      {loading ? (
        <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={18}/></div>
      ) : items.length === 0 ? (
        <div className="fc-card p-10 text-center text-[#A3A3A3]">Nenhum template.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="templates-list">
          {items.map(t => (
            <div key={t.id} className="fc-card p-4" data-testid={`template-${t.code}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[#E5E5E5] text-[14px]">{t.name}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${CATEGORY_BADGE[t.category] || "border-[#262626] text-[#A3A3A3]"}`}>{t.category}</span>
                    {!t.active && <span className="text-[10px] px-2 py-0.5 rounded bg-[#262626] text-[#737373]">Inativo</span>}
                  </div>
                  <div className="text-[10.5px] font-mono-tab text-[#737373] mt-0.5">{t.code}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={()=>{setEditing(t); setShowModal(true);}} className="text-[#A3A3A3] hover:text-[#D4AF37]"><Edit3 size={13}/></button>
                  <button onClick={()=>remove(t.id)} className="text-[#A3A3A3] hover:text-red-300"><Trash2 size={13}/></button>
                </div>
              </div>
              <div className="text-[12.5px] text-[#D0D0D0] whitespace-pre-wrap leading-relaxed">{t.content}</div>
              {t.variables && t.variables.length > 0 && (
                <div className="mt-3 flex gap-1 flex-wrap">
                  {t.variables.map(v => (
                    <code key={v} className="text-[10.5px] px-1.5 py-0.5 rounded bg-[#0a0a0a] border border-[#262626] text-[#D4AF37]">{`{{${v}}}`}</code>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && <TemplateModal editing={editing} categories={categories} onClose={()=>setShowModal(false)} onSaved={()=>{setShowModal(false); load();}}/>}
    </div>
  );
}

function TemplateModal({ editing, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    code: editing?.code || "",
    name: editing?.name || "",
    category: editing?.category || "cobranca",
    content: editing?.content || "",
    active: editing?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");

  const doPreview = async () => {
    try {
      const { data } = await api.post("/whatsapp/render-template", {
        content: form.content,
        variables: { nome:"João Silva", valor:"R$ 1.234,56", juros:"R$ 234,56", vencimento:"2026-08-15", contrato:"CT-001", link_pagamento:"https://pay.forte.com/x" },
      });
      setPreview(data.rendered);
    } catch (err) { toast.error("Falha no preview"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/whatsapp/templates/${editing.id}`, form);
        toast.success("Template atualizado");
      } else {
        await api.post("/whatsapp/templates", form);
        toast.success("Template criado");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="fc-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-modal">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-display text-lg font-bold">{editing ? "Editar" : "Novo"} template</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <label>
            <div className="text-[#A3A3A3] mb-1">Código (único) *</div>
            <input className="fc-input font-mono-tab" value={form.code} onChange={e=>setForm({...form, code:e.target.value})} disabled={!!editing} data-testid="template-code" placeholder="ex: aviso_vencimento"/>
          </label>
          <label>
            <div className="text-[#A3A3A3] mb-1">Nome *</div>
            <input className="fc-input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="template-name"/>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1">Categoria *</div>
            <select className="fc-input" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} data-testid="template-category">
              {categories.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </label>
          <label className="col-span-2">
            <div className="text-[#A3A3A3] mb-1 flex items-center justify-between">
              <span>Conteúdo * <span className="text-[10.5px] text-[#737373]">— use {"{{nome}}"}, {"{{valor}}"}, {"{{juros}}"}, {"{{vencimento}}"}, {"{{contrato}}"}, {"{{link_pagamento}}"}</span></span>
              <button type="button" onClick={doPreview} className="text-[11px] text-emerald-300 hover:text-emerald-200">Preview</button>
            </div>
            <textarea rows={6} className="fc-input" value={form.content} onChange={e=>setForm({...form, content:e.target.value})} data-testid="template-content"/>
          </label>
          {preview && (
            <div className="col-span-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-[12.5px] text-emerald-200 whitespace-pre-wrap">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Preview</div>
              {preview}
            </div>
          )}
          <label className="flex items-center gap-2 col-span-2">
            <input type="checkbox" checked={form.active} onChange={e=>setForm({...form, active:e.target.checked})}/>
            <span>Ativo</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button onClick={save} disabled={saving || !form.code || !form.name || !form.content} className="fc-btn-primary text-xs flex items-center gap-2" data-testid="template-save">
            {saving && <Loader2 size={13} className="animate-spin"/>} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

// =================== HISTÓRICO ===================
function HistoricoTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status:"", origin:"", category:"" });
  const [categories, setCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.origin) params.origin = filters.origin;
      if (filters.category) params.category = filters.category;
      params.limit = 100;
      const { data } = await api.get("/whatsapp/history", { params });
      setItems(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    api.get("/whatsapp/categories").then(r=>setCategories(r.data)).catch(()=>{});
  }, []);
  useEffect(() => { load(); }, [filters]);

  return (
    <div>
      <div className="fc-card p-4 mb-4 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-[#737373] uppercase">Status:</span>
          <select className="fc-input text-xs" value={filters.status} onChange={e=>setFilters({...filters, status:e.target.value})} data-testid="hist-filter-status">
            <option value="">Todos</option>
            <option value="pending">Pendente</option>
            <option value="sent">Enviado</option>
            <option value="delivered">Entregue</option>
            <option value="read">Lido</option>
            <option value="error">Erro</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-[#737373] uppercase">Origem:</span>
          <select className="fc-input text-xs" value={filters.origin} onChange={e=>setFilters({...filters, origin:e.target.value})} data-testid="hist-filter-origin">
            <option value="">Todas</option>
            <option value="manual">Manual</option>
            <option value="bulk">Em massa</option>
            <option value="automatic">Automática</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-[#737373] uppercase">Categoria:</span>
          <select className="fc-input text-xs" value={filters.category} onChange={e=>setFilters({...filters, category:e.target.value})} data-testid="hist-filter-category">
            <option value="">Todas</option>
            {categories.map(c=><option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <button onClick={load} className="fc-btn-ghost text-xs ml-auto flex items-center gap-1.5"><RefreshCcw size={12}/> Atualizar</button>
      </div>

      <div className="text-[12px] text-[#A3A3A3] mb-2" data-testid="hist-total">Total: <span className="text-[#D4AF37] font-mono-tab">{total}</span></div>

      {loading ? (
        <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={18}/></div>
      ) : items.length === 0 ? (
        <div className="fc-card p-10 text-center text-[#A3A3A3]" data-testid="hist-empty">
          <HistoryIcon size={30} className="mx-auto text-[#3a3a3a] mb-3"/>
          Nenhuma mensagem no histórico.
        </div>
      ) : (
        <div className="fc-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="hist-table">
              <thead className="bg-[#0f0f0f] text-[#737373] uppercase text-[10.5px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Data</th>
                  <th className="text-left px-3 py-2 font-medium">Cliente</th>
                  <th className="text-left px-3 py-2 font-medium">Para</th>
                  <th className="text-left px-3 py-2 font-medium">Template</th>
                  <th className="text-left px-3 py-2 font-medium">Categoria</th>
                  <th className="text-left px-3 py-2 font-medium">Origem</th>
                  <th className="text-left px-3 py-2 font-medium">Enviado por</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map(m => (
                  <tr key={m.id} className="border-t border-[#1a1a1a] hover:bg-[#0e0e0e]">
                    <td className="px-3 py-2 font-mono-tab text-[11.5px] text-[#A3A3A3]">{(m.created_at || "").replace("T"," ").slice(0,16)}</td>
                    <td className="px-3 py-2 text-[#E5E5E5]">{m.client_name || "—"}</td>
                    <td className="px-3 py-2 font-mono-tab text-[11.5px]">{m.phone_to || "—"}</td>
                    <td className="px-3 py-2 text-[#A3A3A3]">{m.template_name || m.template_code || "—"}</td>
                    <td className="px-3 py-2">
                      {m.category && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_BADGE[m.category] || ""}`}>{m.category}</span>}
                    </td>
                    <td className="px-3 py-2 text-[#A3A3A3]">{ORIGIN_LABEL[m.origin] || m.origin}</td>
                    <td className="px-3 py-2 text-[#A3A3A3]">{m.sender_name || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_BADGE[m.status] || ""}`}>{STATUS_LABEL[m.status] || m.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
